// Airtable-style filter engine (Dean, 15 Oct 2026) — one model for every list in the portal.
// tree = { join: "and"|"or", items: [ cond | { join, items: [cond] } ] }   cond = { id, field, op, value }
// Fields are declared per page: { key, label, type: text|select|number|date|boolean, get(row) → value, options?: [{value,label}] }

export const OPS = {
  text:    [["is", "is"], ["is_not", "is not"], ["contains", "contains"], ["not_contains", "does not contain"], ["is_empty", "is empty"], ["is_not_empty", "is not empty"]],
  select:  [["is", "is"], ["is_not", "is not"], ["any_of", "is any of"], ["none_of", "is none of"], ["is_empty", "is empty"], ["is_not_empty", "is not empty"]],
  number:  [["eq", "="], ["neq", "≠"], ["lt", "<"], ["gt", ">"], ["between", "is between"], ["is_empty", "is empty"], ["is_not_empty", "is not empty"]],
  date:    [["is", "is"], ["before", "is before"], ["after", "is after"], ["between", "is between"], ["next_days", "is within the next"], ["past_days", "is within the past"], ["is_empty", "is empty"], ["is_not_empty", "is not empty"]],
  boolean: [["is_true", "is yes"], ["is_false", "is no"]],
};
export const DATE_WORDS = [["today", "today"], ["tomorrow", "tomorrow"], ["yesterday", "yesterday"], ["week_start", "start of this week"], ["week_end", "end of this week"], ["month_start", "start of this month"], ["month_end", "end of this month"]];
const NO_VALUE = new Set(["is_empty", "is_not_empty", "is_true", "is_false"]);

let seq = 0;
export const uid = () => `c${Date.now().toString(36)}${(seq++).toString(36)}`;
export const emptyTree = () => ({ join: "and", items: [] });
export const newCond = (field) => ({ id: uid(), field: field?.key || "", op: OPS[field?.type || "text"][0][0], value: field?.type === "date" ? "today" : "" });
export const newGroup = () => ({ id: uid(), join: "or", items: [] });
export const isGroup = (x) => Array.isArray(x?.items);
export const needsValue = (op) => !NO_VALUE.has(op);

export function countConds(tree) {
  return (tree?.items || []).reduce((n, it) => n + (isGroup(it) ? countConds(it) : 1), 0);
}
export const isEmptyTree = (tree) => countConds(tree) === 0;

// ---- dates
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
export function resolveDate(v, today = new Date()) {
  if (!v) return null;
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  switch (v) {
    case "today": return iso(t);
    case "tomorrow": return iso(addDays(t, 1));
    case "yesterday": return iso(addDays(t, -1));
    case "week_start": return iso(addDays(t, -((t.getDay() + 6) % 7)));
    case "week_end": return iso(addDays(t, 6 - ((t.getDay() + 6) % 7)));
    case "month_start": return iso(new Date(t.getFullYear(), t.getMonth(), 1));
    case "month_end": return iso(new Date(t.getFullYear(), t.getMonth() + 1, 0));
    default: return /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : null;
  }
}
const dateOf = (v) => (v ? String(v).slice(0, 10) : "");

// ---- one condition against one row
const me = (v, ctx) => (v === "@me" ? (ctx?.me || "\u0000") : v);   // "@me" = the signed-in user (shared views stay personal)

export function matchCond(cond, row, fieldsByKey, today = new Date(), ctx = {}) {
  const f = fieldsByKey[cond.field];
  if (!f) return true;
  const raw = f.get(row);
  const op = cond.op;
  const empty = raw == null || raw === "" || (Array.isArray(raw) && raw.length === 0);
  if (op === "is_empty") return empty;
  if (op === "is_not_empty") return !empty;
  if (f.type === "boolean") return op === "is_true" ? !!raw : !raw;
  if (f.type === "number") {
    if (empty) return false;
    const n = Number(raw), a = Number(cond.value), b = Number(cond.value2);
    if (op === "eq") return n === a;
    if (op === "neq") return n !== a;
    if (op === "lt") return n < a;
    if (op === "gt") return n > a;
    if (op === "between") return n >= Math.min(a, b) && n <= Math.max(a, b);
    return true;
  }
  if (f.type === "date") {
    const d = dateOf(raw);
    if (!d) return false;
    const t = iso(today);
    if (op === "is") return d === resolveDate(cond.value, today);
    if (op === "before") return d < (resolveDate(cond.value, today) || "");
    if (op === "after") return d > (resolveDate(cond.value, today) || "9999");
    if (op === "between") { const a = resolveDate(cond.value, today), b = resolveDate(cond.value2, today); return !!a && !!b && d >= (a < b ? a : b) && d <= (a < b ? b : a); }
    if (op === "next_days") return d >= t && d <= iso(addDays(today, Number(cond.value) || 0));
    if (op === "past_days") return d <= t && d >= iso(addDays(today, -(Number(cond.value) || 0)));
    return true;
  }
  // text + select — arrays (tags) match when any element matches
  const vals = (Array.isArray(raw) ? raw : [raw]).map((x) => String(x ?? "").toLowerCase());
  const want = String(me(cond.value, ctx) ?? "").toLowerCase();
  const list = (Array.isArray(cond.value) ? cond.value : String(cond.value || "").split("|")).map((x) => String(me(x, ctx)).toLowerCase()).filter(Boolean);
  if (op === "is") return vals.some((v) => v === want);
  if (op === "is_not") return !vals.some((v) => v === want);
  if (op === "contains") return vals.some((v) => v.includes(want));
  if (op === "not_contains") return !vals.some((v) => v.includes(want));
  if (op === "any_of") return vals.some((v) => list.includes(v));
  if (op === "none_of") return !vals.some((v) => list.includes(v));
  return true;
}

export function matchTree(tree, row, fieldsByKey, today = new Date(), ctx = {}) {
  const items = tree?.items || [];
  if (!items.length) return true;
  const test = (it) => (isGroup(it) ? matchTree(it, row, fieldsByKey, today, ctx) : matchCond(it, row, fieldsByKey, today, ctx));
  return tree.join === "or" ? items.some(test) : items.every(test);
}

export function applyFilter(rows, tree, fields, ctx = {}) {
  if (isEmptyTree(tree)) return rows;
  const byKey = Object.fromEntries(fields.map((f) => [f.key, f]));
  const today = new Date();
  return rows.filter((r) => matchTree(tree, r, byKey, today, ctx));
}

// ---- labels for chips
const opLabel = (type, op) => (OPS[type] || OPS.text).find(([k]) => k === op)?.[1] || op;
export function condLabel(cond, fieldsByKey) {
  const f = fieldsByKey[cond.field];
  if (!f) return "?";
  const opt = (v) => (v === "@me" ? "me" : f.options?.find((o) => String(o.value) === String(v))?.label ?? v);
  let val = "";
  if (needsValue(cond.op)) {
    if (cond.op === "between") val = `${opt(cond.value)} and ${opt(cond.value2)}`;
    else if (cond.op === "next_days" || cond.op === "past_days") val = `${cond.value || 0} days`;
    else if (cond.op === "any_of" || cond.op === "none_of") val = String(cond.value || "").split("|").filter(Boolean).map(opt).join(", ");
    else if (f.type === "date") val = DATE_WORDS.find(([k]) => k === cond.value)?.[1] || cond.value || "…";
    else val = opt(cond.value) || "…";
  }
  return `${f.label} ${opLabel(f.type, cond.op)}${val ? ` ${val}` : ""}`;
}

// ---- URL round-trip (base64url JSON, ids dropped)
const strip = (t) => ({ join: t.join, items: (t.items || []).map((it) => (isGroup(it) ? strip(it) : { field: it.field, op: it.op, value: it.value, ...(it.value2 != null ? { value2: it.value2 } : {}) })) });
const hydrate = (t) => ({ id: uid(), join: t.join === "or" ? "or" : "and", items: (t.items || []).map((it) => (isGroup(it) ? hydrate(it) : { id: uid(), ...it })) });
export const normaliseTree = (t) => (t && Array.isArray(t.items) ? hydrate(t) : emptyTree());
export function encodeTree(tree) {
  if (isEmptyTree(tree)) return "";
  const s = JSON.stringify(strip(tree));
  return btoa(unescape(encodeURIComponent(s))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
export function decodeTree(s) {
  if (!s) return null;
  try {
    const b = s.replace(/-/g, "+").replace(/_/g, "/");
    return hydrate(JSON.parse(decodeURIComponent(escape(atob(b)))));
  } catch { return null; }
}
export const sameTree = (a, b) => JSON.stringify(strip(a || emptyTree())) === JSON.stringify(strip(b || emptyTree()));
export const bareTree = strip;
